import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import JsonLd from "@/components/JsonLd";
import { faqSchema } from "@/lib/seo-schemas";
import { FaqItem, toFaqItems } from "@/components/FaqSection";

export interface FaqGroup {
  key: string;
  title: string;
  items: FaqItem[];
}

/** Convert i18n raw { title, items: [{q,a}] } to a FaqGroup. */
export function toFaqGroup(key: string, raw: unknown): FaqGroup | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as { title?: string; items?: unknown };
  if (!obj.title || !Array.isArray(obj.items)) return null;
  const items = toFaqItems(obj.items);
  if (!items.length) return null;
  return { key, title: obj.title, items };
}

interface FaqGroupsSectionProps {
  title: string;
  groups: FaqGroup[];
  className?: string;
}

/**
 * Renders multiple categorized FAQ groups (each with its own heading + accordion)
 * and emits a single FAQPage JSON-LD covering every question on the page.
 */
const FaqGroupsSection = ({ title, groups, className = "" }: FaqGroupsSectionProps) => {
  const validGroups = groups.filter((g) => g.items.length > 0);
  if (!validGroups.length) return null;
  const allItems = validGroups.flatMap((g) => g.items);

  return (
    <section className={`bg-card py-16 md:py-24 ${className}`}>
      <JsonLd data={faqSchema(allItems)} />
      <div className="container max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-12">
          {title}
        </h2>
        <div className="space-y-10">
          {validGroups.map((group) => (
            <div key={group.key}>
              <h3
                id={`faq-${group.key}`}
                className="text-xl md:text-2xl font-bold font-heading mb-4 text-foreground scroll-mt-24"
              >
                {group.title}
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {group.items.map((item, i) => (
                  <AccordionItem key={i} value={`${group.key}-${i}`}>
                    <AccordionTrigger className="text-left text-base md:text-lg font-bold">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-foreground/80 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqGroupsSection;
